import { Button, doPasswordsMatch, isPasswordValid, PageHeader, PasswordConditions, Textfield } from '@flexo/atoms';
import { getSavedLang, httpService, IconWrapper, REGEX, triggerKeychainSave } from '@flexo/general';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './forgot-password-mobile.module.scss';

import { Keyboard } from '@capacitor/keyboard';
import { useAuth, useClerk, useSignIn } from '@clerk/clerk-react';
import { useFormValidation } from '@flexo/hooks';
import { KeyboardAwareLayout } from '@flexo/organisms';
import { ALERT } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import { useDispatch } from 'react-redux';

export enum ForgotPasswordStage {
  EnterEmail = 1,
  EnterRecoveryCode = 2,
  ResetPassword = 3,
}

export interface InputErrors {
  email: boolean;
  code: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

export interface ForgotPasswordProps {
  containedLabelTextfield: boolean;
  textfieldColor?: 'white' | 'grey' | 'light' | 'primary' | 'secondary' | 'transparent';
}

export function ForgotPassword({ containedLabelTextfield, textfieldColor }: ForgotPasswordProps) {
  const ENV = (import.meta as any).env;

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { signIn, setActive } = useSignIn();
  const { getToken } = useAuth();
  const clerk = useClerk();

  const savedLang = getSavedLang() || null;
  const browserLanguage = savedLang?.split('_')[1] || navigator.language.split('-')[0] || ENV.VITE_APP_LANGUAGE;

  const [email, setEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentStage, setCurrentStage] = useState<ForgotPasswordStage>(
    ForgotPasswordStage.EnterEmail
  );
  const [hasResent, setHasResent] = useState(false);

  const loader = '/loaderLottie.json';
  const [loading, setLoading] = useState(false);

  const [inputType, setInputType] = useState({
    newPassword: 'password',
    confirmPassword: 'password',
  });
  const [icon, setIcon] = useState({
    newPassword: 'visibility',
    confirmPassword: 'visibility',
  });

  // Email validation function
  const isEmail = useCallback((email: string) => {
    return REGEX.email.test(email);
  }, []);

  // Form validation hook
  const {
    errors,
    blur,
    passwordBlur,
    passwordValidation,
    handleBlur,
    handleFocus,
    handlePasswordValidation,
  } = useFormValidation(
    {
      fields: ['email', 'code', 'newPassword', 'confirmPassword'],
      validationRules: {
        email: {
          validate: (value) => isEmail(value),
        },
        code: {
          validate: (value) => value.length >= 4,
        },
      },
      hasPasswordFields: true,
    },
    { email, recoveryCode, newPassword, confirmPassword }
  );

  // Create refs for all input fields
  const emailRef = useRef<HTMLInputElement>(null);
  const recoveryCodeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // Clean up keyboard on unmount and reset resent state
  useEffect(() => {
    return () => {
      Keyboard.hide();
      setHasResent(false); // Reset resent state on unmount
    };
  }, []);

  // Reset resent state when stage changes away from recovery code
  useEffect(() => {
    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) {
      setHasResent(false);
    }
  }, [currentStage]);

  // Focus appropriate input field when stage changes
  useEffect(() => {
    // Using a timeout to ensure DOM is ready and layout is stable
    const focusTimer = setTimeout(() => {
      let targetRef: React.RefObject<HTMLInputElement> | null = null;

      if (currentStage === ForgotPasswordStage.EnterEmail) {
        targetRef = emailRef;
      } else if (currentStage === ForgotPasswordStage.EnterRecoveryCode) {
        targetRef = recoveryCodeRef;
      } else if (currentStage === ForgotPasswordStage.ResetPassword) {
        targetRef = passwordRef;
      }

      if (targetRef?.current) {
        targetRef.current.focus();
      }
    }, 250);

    return () => clearTimeout(focusTimer);
  }, [currentStage]);

  // Pure validation functions for internal use (no side effects)
  const isValidEmail = useCallback(
    (email: string) => {
      return isEmail(email);
    },
    [isEmail]
  );

  const isValidRecoveryCode = useCallback((code: string) => {
    return code.length >= 4;
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      switch (name) {
        case 'email':
          setEmail(value);
          break;
        case 'recoveryCode':
          setRecoveryCode(value);
          break;
        case 'newPassword':
          setNewPassword(value);
          handlePasswordValidation(value, confirmPassword);
          break;
        case 'confirmPassword':
          setConfirmPassword(value);
          handlePasswordValidation(newPassword, value);
          break;
      }
    },
    [newPassword, confirmPassword, handlePasswordValidation]
  );

  const requestRecoveryCode = useCallback(async () => {
    if (!isEmail(email)) {
      return;
    }
    setLoading(true);
    try {
      // Use new Clerk-based auth endpoint
      const response = await httpService.post({
        url: `${ENV.VITE_APP_CUSTOM_BASE_URL || ENV.VITE_APP_BASE_URL}/common/auth/v1/password-forgot`,
        data: { email: email, locale: browserLanguage, organization_slug: ENV.VITE_APP_CLERK_SLUG },
      });
      if (response.status === 200)
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
    } catch (error) {
      console.error('API call failed:', error);
      dispatch(
        ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
      );
    } finally {
      setLoading(false);
    }
  }, [email, ENV.VITE_APP_BASE_URL, isEmail, dispatch, browserLanguage]);

  const verifyRecoveryCode = useCallback(async () => {
    // In the new Clerk flow, we don't verify the code here.
    // The temporary password is verified when we login with Clerk in the resetPassword step.
    // Just proceed to the next stage.
    if (!recoveryCode) {
      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_INVALID_CREDENTIALS',
            } as any,
          },
        } as any)
      );
      return;
    }

    setCurrentStage(ForgotPasswordStage.ResetPassword);
  }, [recoveryCode, dispatch]);

  const resetPassword = useCallback(async () => {
    if (newPassword !== confirmPassword) return;
    if (!signIn) {
      console.error('Clerk signIn not available');
      dispatch(ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any }));
      return;
    }
    setLoading(true);

    try {
      // Step 1: Login with Clerk using email + temporary password (recoveryCode)
      const signInResult = await signIn.create({
        identifier: email,
        password: recoveryCode, // The temporary password sent via email
      });

      if (signInResult.status !== 'complete') {
        // Login failed - invalid temporary password
        console.error('Clerk login failed:', signInResult.status);
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_INVALID_CREDENTIALS' } as any }));
        return;
      }

      // Step 2: Set the active session
      await setActive({ session: signInResult.createdSessionId });

      // Step 2b: Set the active organization for this app
      // Poll for organization memberships — Clerk loads them asynchronously after
      // setActive(session), and with the link flow (deep link from email) the client
      // is freshly initialized so 300ms is often not enough.
      // Total budget: 20 * 300ms = 6s, so end-users with slower membership hydration
      // don't fall through to BANNER_UNEXPECTED_ERROR before Clerk has loaded data.
      const targetOrgId = ENV.VITE_APP_CLERK_ORG_ID;
      let orgMembership: any = null;
      for (let attempt = 0; attempt < 20; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const memberships = clerk.user?.organizationMemberships || [];
        orgMembership = targetOrgId
          ? memberships.find((m: any) => m.organization.id === targetOrgId) || memberships[0]
          : memberships[0];
        if (orgMembership?.organization) break;
      }

      if (!orgMembership?.organization) {
        console.error('Could not resolve organization membership after login');
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any }));
        setLoading(false);
        return;
      }

      await clerk.setActive({ organization: orgMembership.organization.id });

      // Step 3: Get a fresh JWT token
      const token = await getToken();
      if (!token) {
        throw new Error('Failed to get JWT token after login');
      }

      // Step 4: Call backend to change password to the new one
      const response = await httpService.post({
        url: `${ENV.VITE_APP_CUSTOM_BASE_URL || ENV.VITE_APP_BASE_URL}/common/auth/v1/password-change`,
        data: {
          new_password: newPassword,
          locale: browserLanguage,
        },
        idToken: token,
        skipLogoutOn401: true,
      });

      if (response.status === 200) {
        triggerKeychainSave(email, newPassword);
        localStorage.setItem('passwordUpdated', 'true');
        sessionStorage.setItem('passwordUpdated', 'true');
        navigate('/login');
      }
    } catch (error: any) {
      console.error('Password reset failed:', error);

      // Check if error is from Clerk login (invalid temp password)
      if (error?.errors?.[0]?.code === 'form_password_incorrect' ||
          error?.errors?.[0]?.code === 'form_identifier_not_found') {
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_INVALID_CREDENTIALS' } as any }));
      } else {
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any }));
      }
    } finally {
      setLoading(false);
    }
  }, [
    email,
    recoveryCode,
    newPassword,
    confirmPassword,
    browserLanguage,
    signIn,
    setActive,
    clerk,
    getToken,
    navigate,
    dispatch,
  ]);

  const togglePasswordVisibility = useCallback((name: string) => {
    setInputType((p) => ({
      ...p,
      [name]: p[name] === 'password' ? 'text' : 'password',
    }));
    setIcon((p) => ({
      ...p,
      [name]: p[name] === 'visibility' ? 'hide-visibility' : 'visibility',
    }));
  }, []);

  const handleResendCode = useCallback(async () => {

    await requestRecoveryCode();
    setHasResent(true);
  }, [requestRecoveryCode, email, isEmail]);

  const getSubtitleText = useCallback(() => {
    if (currentStage === ForgotPasswordStage.EnterEmail)
      return t('pages.forgotten_password.subtitle');
    if (currentStage === ForgotPasswordStage.EnterRecoveryCode) {
      // For recovery code stage, we'll handle the subtitle in a custom component
      return '';
    }
    if (currentStage === ForgotPasswordStage.ResetPassword)
      return t('pages.forgotten_password.subtitle_change', { email });
    return '';
  }, [currentStage, t, email]);

  const renderCustomSubtitle = useCallback(() => {
    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) return null;

    const line1 = t('pages.forgotten_password.recoveryCode_subtitle.line_1');
    const line2 = t('pages.forgotten_password.recoveryCode_subtitle.line_2');
    const line3 = t('pages.forgotten_password.recoveryCode_subtitle.line_3');

    return (
      <div className={`paragraph ${styles.ForgotPassword__Subtitle}`}>
        {line1} <strong className={styles.ForgotPassword__Subtitle__Email}>{email}</strong>
        {line2} {line3}
        {`${line2} ${line3} `}
        {hasResent ? (
          <span className={styles.ForgotPassword__Subtitle__ResentLabel}>
            {t('pages.forgotten_password.recoveryCode_subtitle.resentLabel')}
            <IconWrapper
              iconName="check-encircled_outline"
              className={styles.ForgotPassword__Subtitle__ResentIcon}
            />
          </span>
        ) : (
          <button
            onClick={handleResendCode}
            className={styles.ForgotPassword__Subtitle__ResendButton}
          >
            {t('pages.forgotten_password.recoveryCode_subtitle.buttonLabel')}
          </button>
        )}
      </div>
    );
  }, [currentStage, t, email, hasResent, handleResendCode]);
  const stageConfig = useMemo(
    () => ({
      [ForgotPasswordStage.EnterEmail]: {
        buttonLabel: t('pages.forgotten_password.sendButton'),
        action: requestRecoveryCode,
        isDisabled: () => !email || !isValidEmail(email) || loading,
        inputComponent: (
          <Textfield
            name="email"
            ref={emailRef}
            variant={containedLabelTextfield ? "containedLabel" : "default"}
            value={email}
            placeholder={
              t('pages.forgotten_password.email_placeholder') as string
            }
            label={t('pages.forgotten_password.email') as string}
            onChange={handleInputChange}
            type="email"
            autoComplete="email"
            error={errors.email && blur.email}
            errorDescription={
              errors.email && blur.email
                ? (t('pages.forgotten_password.email_error') as string)
                : ''
            }
            onBlur={handleBlur}
            onFocus={handleFocus}
            key="email-input"
            textfieldColor={textfieldColor || 'white'}
          />
        ),
      },
      [ForgotPasswordStage.EnterRecoveryCode]: {
        buttonLabel: t('pages.forgotten_password.verifyButton'),
        action: verifyRecoveryCode,
        isDisabled: () =>
          !recoveryCode || !isValidRecoveryCode(recoveryCode) || loading,
        inputComponent: (
          <Textfield
            name="recoveryCode"
            ref={recoveryCodeRef}
            value={recoveryCode}
            variant={containedLabelTextfield ? "containedLabel" : "default"}
            textfieldColor={textfieldColor || 'white'}
            placeholder={
              t('pages.forgotten_password.recoveryCode_placeholder') as string
            }
            label={t('pages.forgotten_password.recoveryCode') as string}
            onChange={handleInputChange}
            type="text"
            error={errors.code && blur.code}
            errorDescription={
              errors.code && blur.code
                ? (t('pages.forgotten_password.code_error') as string)
                : ''
            }
            onBlur={handleBlur}
            onFocus={handleFocus}
            key="code-input"
          />
        ),
      },
      [ForgotPasswordStage.ResetPassword]: {
        buttonLabel: t('pages.forgotten_password.resetButton'),
        action: resetPassword,
        isDisabled: () => {
          if (!newPassword || !confirmPassword) return true;
          return (
            !isPasswordValid(newPassword) ||
            !doPasswordsMatch(newPassword, confirmPassword)
          );
        },
        inputComponent: (
          <form onSubmit={(e) => { e.preventDefault(); resetPassword(); }}>
            {/* Hidden email field for keychain/password manager capture */}
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              readOnly
              style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
              tabIndex={-1}
            />
            <Textfield
              name="newPassword"
              ref={passwordRef}
              value={newPassword}
              variant={containedLabelTextfield ? "containedLabel" : "default"}
              textfieldColor={textfieldColor || 'white'}
              placeholder={
                t('pages.forgotten_password.newPassword_placeholder') as string
              }
              label={t('pages.forgotten_password.newPassword') as string}
              onChange={handleInputChange}
              type={inputType.newPassword}
              icon={icon.newPassword}
              onIconClick={() => togglePasswordVisibility('newPassword')}
              onBlur={handleBlur}
              onFocus={handleFocus}
              autoComplete="new-password"
              error={errors.newPassword && blur.newPassword}
              errorDescription={
                errors.newPassword && blur.newPassword
                  ? 'Please review the password criteria'
                  : ''
              }
              key="password-input"
            />
            <PasswordConditions
              validation={passwordValidation}
              isFocused={!passwordBlur}
              conditionClassName={styles.ForgotPassword__Conditions}
            />
            <Textfield
              name="confirmPassword"
              ref={confirmPasswordRef}
              value={confirmPassword}
              variant={containedLabelTextfield ? "containedLabel" : "default"}
              textfieldColor={textfieldColor || 'white'}
              placeholder={
                t(
                  'pages.forgotten_password.confirmPassword_placeholder'
                ) as string
              }
              label={t('pages.forgotten_password.confirmPassword') as string}
              onChange={handleInputChange}
              type={inputType.confirmPassword}
              icon={icon.confirmPassword}
              onIconClick={() => togglePasswordVisibility('confirmPassword')}
              onBlur={handleBlur}
              autoComplete="off"
              error={errors.confirmPassword && blur.confirmPassword}
              errorDescription={
                errors.confirmPassword && blur.confirmPassword
                  ? t('pages.forgotten_password.confirmPassword_error') as string
                  : ''
              }
              onFocus={handleFocus}
              key="confirm-password-input"
            />
          </form>
        ),
      },
    }),
    [
      t,
      email,
      isValidEmail,
      isValidRecoveryCode,
      isPasswordValid,
      doPasswordsMatch,
      handleInputChange,
      handleBlur,
      errors,
      recoveryCode,
      newPassword,
      confirmPassword,
      passwordValidation,
      inputType,
      icon,
      togglePasswordVisibility,
      blur,
      handleFocus,
      passwordBlur,
      requestRecoveryCode,
      verifyRecoveryCode,
      resetPassword,
      loading,
    ]
  );

  const currentStageConfig = useMemo(
    () => stageConfig[currentStage],
    [stageConfig, currentStage]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !currentStageConfig.isDisabled() && !loading)
        currentStageConfig.action();
    },
    [currentStageConfig, loading]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Header component for the keyboard-aware layout
  const headerComponent = (
    <div className={styles.ForgotPassword__Header}>
      <PageHeader
        title={t('pages.forgotten_password.title')}
        subtitle={getSubtitleText()}
        back={t('pages.forgotten_password.backButton') || ''}
        backAction={() => {
          if (currentStage === ForgotPasswordStage.EnterEmail) navigate(-1);
          else setCurrentStage((prevStage) => prevStage - 1);
        }}
      />
      {renderCustomSubtitle()}
    </div>
  );

  // Footer component with the action button
  const footerComponent = (
    <div className={styles.ForgotPassword__Footer}>
      <Button
        size='large'
        onClick={currentStageConfig.action}
        variant="primary"
        color="secondary"
        className={styles.ForgotPassword__Button}
        label={
          loading ? (
            <Player autoplay loop src={loader} style={{ height: 20 }} />
          ) : (
            currentStageConfig.buttonLabel
          )
        }
        disabled={currentStageConfig.isDisabled() || loading}
      />
    </div>
  );

  // Main content with input fields
  const mainContent = (
    <div className={styles.ForgotPassword__Inputs}>
      <div key={`stage-${currentStage}`}>
        {currentStageConfig.inputComponent}
      </div>
    </div>
  );

  return (
    <div className={styles.ForgotPassword}>
      <KeyboardAwareLayout
        header={headerComponent}
        footer={footerComponent}
        extraPadding={0}
        contentClassName={styles.ForgotPassword__Content}
      >
        {mainContent}
      </KeyboardAwareLayout>
    </div>
  );
}

export default ForgotPassword;
